import React from 'react';
import type { AdminTenantOption } from '../AdminTenantSelector';
import type { OverallState, PermissionsPolicy, PostureSummary } from './types';

interface PermissionsPageHeroProps {
  tenants: AdminTenantOption[];
  selectedTenantId: string;
  onSelectTenant: (tenantId: string) => void;
  policy: PermissionsPolicy | null;
  summary: PostureSummary | null;
  generatedAtUtc: string | null;
  overallState: OverallState;
  /**
   * When true and there is more than one covered tenant, the picker
   * grows a synthetic "Enterprise baseline" entry that switches the
   * tabs to operate on the baseline layer instead of any one tenant.
   * Phase-1 plumbing only — actual selection wired in commit 5.
   */
  enterpriseBaselineAvailable?: boolean;
  baselineLayerInFocus?: boolean;
  onSelectBaselineLayer?: () => void;
}

const overallStateLabel = (state: OverallState): string => {
  if (state === 'healthy') return 'Healthy';
  if (state === 'warning') return 'Warning';
  return 'At risk';
};

const formatTimeAgo = (iso: string | null): string => {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  const seconds = Math.floor((Date.now() - parsed.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  return `${Math.floor(seconds / 86400)} days ago`;
};

const formatPolicyVersion = (policy: PermissionsPolicy | null): string => {
  if (!policy || !policy.updatedAt) {
    return 'no published policy yet';
  }
  const when = formatTimeAgo(policy.updatedAt);
  const who = policy.updatedBy ? ` by ${policy.updatedBy}` : '';
  return `last saved ${when}${who}`;
};

/**
 * Page-wide hero for /permissions. Title, tenant chip, picker (only when
 * the signed-in admin covers more than one tenant), active policy version,
 * overall state, and the four-chip summary row. Severity tint follows the
 * overall state per the design doc.
 */
export const PermissionsPageHero: React.FC<PermissionsPageHeroProps> = ({
  tenants,
  selectedTenantId,
  onSelectTenant,
  policy,
  summary,
  generatedAtUtc,
  overallState,
  enterpriseBaselineAvailable = false,
  baselineLayerInFocus = false,
  onSelectBaselineLayer
}) => {
  const selectedTenant = tenants.find((tenant) => tenant.tenantId === selectedTenantId) ?? null;
  const tenantName = baselineLayerInFocus ? 'Enterprise baseline' : selectedTenant?.displayName ?? 'Tenant resolving…';
  const showPicker = tenants.length > 1;
  const severityClass =
    overallState === 'healthy'
      ? 'severity-success'
      : overallState === 'warning'
      ? 'severity-warning'
      : 'severity-danger';
  const healthy = summary?.healthyInstances ?? 0;
  const drift = summary?.missingCriticalGroupInstances ?? 0;
  const stale = summary?.staleInstances ?? 0;
  const notChecked = (summary?.totalInstances ?? 0) - (summary?.inspectableInstances ?? 0);

  return (
    <header className={`permissions-page-hero ${severityClass}`}>
      <div className="hero-row">
        <h1 className="hero-title">Permissions</h1>
        <span className="hero-tenant" title={selectedTenant?.tenantId ?? ''}>{tenantName}</span>
        {showPicker && (
          <select
            className="hero-picker input"
            value={baselineLayerInFocus ? '__baseline__' : selectedTenantId}
            onChange={(event) => {
              const value = event.target.value;
              if (value === '__baseline__' && onSelectBaselineLayer) {
                onSelectBaselineLayer();
              } else {
                onSelectTenant(value);
              }
            }}
            aria-label="Choose tenant or Enterprise baseline"
          >
            {tenants.map((tenant) => (
              <option key={tenant.tenantId} value={tenant.tenantId}>{tenant.displayName}</option>
            ))}
            {enterpriseBaselineAvailable && (
              <option value="__baseline__">Enterprise baseline (all tenants)</option>
            )}
          </select>
        )}
        <span className="hero-spacer" />
        <span className={`hero-state-pill ${severityClass}`}>{overallStateLabel(overallState)}</span>
      </div>

      <div className="hero-meta">
        <span className="hero-policy">Policy {formatPolicyVersion(policy)}</span>
        {generatedAtUtc && (
          <span className="hero-meta-dot" aria-hidden="true">·</span>
        )}
        {generatedAtUtc && (
          <span className="hero-snapshot">Snapshot {formatTimeAgo(generatedAtUtc)}</span>
        )}
      </div>

      <div className="hero-chips">
        <span className="hero-chip"><strong>{healthy}</strong> healthy</span>
        <span className="hero-chip"><strong>{drift}</strong> drift</span>
        <span className="hero-chip"><strong>{stale}</strong> stale</span>
        <span className="hero-chip"><strong>{notChecked}</strong> not checked</span>
      </div>
    </header>
  );
};

export default PermissionsPageHero;
